// TableHeader.tsx - Converted to div-based layout
import { Fragment, useEffect, useState } from "react";
import TableBody from "./TableBody";
import { TableDetailsItem, tableMinWidth } from "./tableFunctions";
import Image from "../images/Image";
import { getFirefoxCssFix } from "../commonfunctions/getFirefoxCssFix";
import useTranslation from "../CustomHooks/useTranslation";
import { adminContents } from "../../pages/AdminManagement/components/AdminManagementContent";

interface TableHeaderProps {
  loading: boolean;
  TableDetails: TableDetailsItem;
  handleActions: (id: string, key: string, text?: string) => void;
  handleSort?: (key: string) => void;
  getList?: () => void;
  searchParams?: URLSearchParams;
  listData?: unknown[];
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  mnoList?: boolean;
  amountList?: boolean;
  TabChildren?: boolean;
  mainEndPoint?: string;
  stickyStyleTableHeader?: string;
}

const TableHeader = ({
  loading,
  listData,
  TableDetails,
  handleActions,
  searchParams,
  handleSort,
  getList,
  hasActivateDeactivateMDPrivilege,
  hasViewMDPrivilege,
  hasEditMDPrivilege,
  mnoList,
  amountList,
  mainEndPoint,
  TabChildren,
}: TableHeaderProps) => {
  const [isSafari, setIsSafari] = useState(false);
  const tableHeaders = Object.entries(TableDetails);
  const currentSort = searchParams?.get("sort");
  const { translate, notEnglish } = useTranslation(adminContents);

  
  useEffect(() => {
    // Detect Safari browser
    const isSafariBrowser = /^((?!chrome|android).)*safari/i.test(
      navigator.userAgent
    );
    setIsSafari(isSafariBrowser);
  }, []);

  const getSortOrder = (key: string) => {
    if (currentSort) {
      const sortArray = currentSort.split(",");
      const existingSort = sortArray.find((s) => s.startsWith(key));
      if (existingSort) {
        return existingSort.endsWith("asc") ? "asc" : "desc";
      }
    }
    return null;
  };

  // Function to get Safari-specific styles
  const getSafariStyles = (isHeader: boolean) => {
    if (!isSafari) return "";
    return isHeader ? "border-b border-[#D8DFE9]" : "";
  };

  // Function to handle RTL layout for Safari
  const getHeaderOrder = (index: number) => {
    if (!isSafari || !notEnglish) return "";
    return `order-${tableHeaders.length - index}`;
  };

  // Create grid template columns based on table headers
  const getGridTemplateColumns = () => {
    const columns = tableHeaders.map(([, details]) => {
      if (details.id === "actions") {
        return "120px"; // Fixed width for actions
      }
      // Convert min-width classes to actual values with better distribution
      const minWidthClass = tableMinWidth(details.id);
      if (minWidthClass.includes("110px")) return "minmax(110px, 0.9fr)"; // Company ID - smaller
      if (minWidthClass.includes("120px")) return "minmax(120px, 1fr)";   // User/USSD code - medium
      if (minWidthClass.includes("220px")) return "minmax(220px, 1.6fr)"; // Company Name - medium-large
      if (minWidthClass.includes("250px")) return "minmax(250px, 1.8fr)"; // Parent User Code and Name - large
      if (minWidthClass.includes("200px")) return "minmax(200px, 1.4fr)"; // Date fields - medium-large
      if (minWidthClass.includes("160px")) return "minmax(160px, 1.2fr)"; // Transaction ID - medium
      if (minWidthClass.includes("150px")) return "minmax(150px, 1.2fr)"; // Phone number - medium
      if (minWidthClass.includes("140px")) return "minmax(140px, 1fr)"; // Transaction category - medium
      if (minWidthClass.includes("280px")) return "minmax(280px, 2.2fr)"; // Email - larger
      if (minWidthClass.includes("130px")) return "minmax(130px, 1fr)"; // Status - medium
      if (minWidthClass.includes("160px")) return "minmax(160px, 1.2fr)"; // Transaction status - medium
      if (minWidthClass.includes("280px") && details.id === "user_location") return "minmax(280px, 1.8fr)"; // User location - large
      if (minWidthClass.includes("110px")) return "minmax(110px, 0.8fr)"; // User name - smaller
            if (minWidthClass.includes("450px")) return "minmax(450px, 3.5fr)"; // On Target Service Fee Percentage - extra large
      if (minWidthClass.includes("400px")) return "minmax(400px, 3fr)"; // Achievement Amount Received - very large
      if (minWidthClass.includes("320px")) return "minmax(320px, 2.5fr)"; // Reference - extra large
      if (minWidthClass.includes("280px")) return "minmax(280px, 2.2fr)"; // Name - large
      if (minWidthClass.includes("250px")) return "minmax(250px, 2fr)"; // Service Fee Received Date - large
      if (minWidthClass.includes("203px")) return "minmax(203px, 1.5fr)"; // Contract dates - medium-large
              if (minWidthClass.includes("200px") && details.id?.includes("balance")) return "minmax(200px, 1.5fr)"; // Balance fields - medium-large
      return "minmax(200px, 1.5fr)"; // Default - medium-large
    });
    return columns.join(" ");
  };

  return (
    <div
      className={`block ${TabChildren
        ? loading
          ? "h-childernTabHight2 overflow-hidden"
          : `h-childernTabHight overflow-auto ${getFirefoxCssFix("scroll")}`
        : amountList
          ? loading
            ? "overflow-hidden"
            : `${mainEndPoint === 'cash-in' ? 'h-amountTableHeight1 overflow-y-auto overflow-x-hidden' : 'h-amountTableHeight overflow-auto'}  ${getFirefoxCssFix("scroll")}`
          : loading
            ? "overflow-hidden"
            : `h-tableHeight ${getFirefoxCssFix("scroll")}`
        } ${tableHeaders.length - 1 && loading ? "" : ""
        }`}
    >
      <div className="w-full max-h-full scrollBar border border-[#D8DFE9] overflow-auto ">
      <div className="min-w-full">
        {/* Header Row */}
        <div
          className="sticky top-0 z-[15] grid"
          style={{ gridTemplateColumns: getGridTemplateColumns() }}
          dir={notEnglish && !isSafari ? "rtl" : "ltr"}
        >
          {tableHeaders.map(([header, details], indexTable = 0) => (
            <Fragment key={indexTable + "indexTable"}>
              {details.id !== "actions" ? (
                <div
                  className={`text-nowrap text-left relative bg-p_light border border-[#D8DFE9] w-full
                    ${getFirefoxCssFix("tableBorder")} 
                    text-large 
                    ${tableMinWidth(details.id)} 
                    ${details.className || ""}
                    ${getSafariStyles(true)}
                    ${getHeaderOrder(indexTable)}
                    ${notEnglish && isSafari ? "flex-1" : ""}
                  `}
                  data-testid={`header-${details.id}`}
                >
                  <div className="flex items-center py-[10px] px-1.5">
                    {loading ? (
                      <div className="h-[24px] w-full bg-gray-200 rounded animate-pulse"></div>
                    ) : (
                      <>
                        {translate(header)}
                        {details.sort && (
                          <div
                            className={`ml-2 ${getSortOrder(details.id) === "desc"
                              ? "rotate-180"
                              : ""
                              }`}
                          >
                            <Image
                              src={
                                getSortOrder(details.id)
                                  ? "sort"
                                  : "inactive_sort"
                              }
                              testId={`sort_${details.id}`}
                              onClick={() =>
                                handleSort ? handleSort(details.id) : {}
                              }
                              className={`h-5 w-5 ${!loading ? "cursor-pointer" : ""
                                }`}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className={`${loading ? '' : 'sticky'} top-0 text-center bg-p_light border border-[#D8DFE9] w-full
                    text-large ${loading ? '' : 'right-0'} z-[20]
                    ${notEnglish && isSafari ? "flex-none" : ""}
                  `}
                  data-testid={`header-${details.id}`}
                >
                  <div
                    className={`text-center ${loading ? 'px-2' : 'px-4'} py-[10px] flex justify-center`}
                  >
                    {loading ? (
                      <div className="h-[24px] w-full bg-gray-200 rounded animate-pulse"></div>
                    ) : (
                      details?.actionTypes.length > 1
                        ? translate("Actions")
                        : translate("Action")
                    )}
                  </div>
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {/* Body Rows */}
        <div className="relative">
          {loading ? (
            // Skeleton loading rows
            Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className="grid"
                style={{ gridTemplateColumns: getGridTemplateColumns() }}
              >
                {tableHeaders.map(([key, details]) => (
                  <div
                    key={`${key}-${index}`}
                    className={`animation-plus text-body2 bg-white border border-[#D8DFE9] ${mainEndPoint?.includes('service-fee') ? 'p-2' : 'p-1.5'} 
                      ${tableMinWidth(details.id)} 
                      ${details.className || ""}`}
                  >
                    <div className="h-[30px] bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ))
          ) : listData && listData.length > 0 ? (
            <>
              {listData.map((item: unknown, index) => (
                <div
                  key={index}
                  className="grid hover:bg-gray-50"
                  style={{ gridTemplateColumns: getGridTemplateColumns() }}
                >
                  <TableBody
                    tableBodyItem={item as Record<string, unknown>}
                    TableDetails={TableDetails}
                    tableBodyIndex={index}
                    handleActions={handleActions}
                    getList={getList}
                    hasActivateDeactivateMDPrivilege={
                      hasActivateDeactivateMDPrivilege
                    }
                    listData={listData}
                    hasViewMDPrivilege={hasViewMDPrivilege}
                    hasEditMDPrivilege={hasEditMDPrivilege}
                    mnoList={mnoList}
                    mainEndPoint={mainEndPoint}
                    advanceStatus={(item as Record<string, unknown>)?.status === "Rejected"}
                  />
                </div>
              ))}
            </>
          ) : (
            <div
              className="grid py-4"
              style={{ gridTemplateColumns: getGridTemplateColumns() }}
            >
              <div 
                className="text-center py-4 bg-white border border-[#D8DFE9]"
                style={{ gridColumn: `1 / ${tableHeaders.length + 1}` }}
              >
                {translate("No data available")}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      
    </div>
  );
};

export default TableHeader;
